import { useState, useEffect, useRef } from 'react';
import { Editor } from '@tiptap/react';

interface UsePageCalculationProps {
    editor: Editor | null;
    contentLoaded: boolean;
}

export const usePageCalculation = ({ editor, contentLoaded }: UsePageCalculationProps) => {
    const [pageCount, setPageCount] = useState(1);
    const editorContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!editor || !contentLoaded) {
            return;
        }

        const calculatePages = () => {
            try {
                if (!editorContainerRef.current) {
                    return;
                }

                const editorContent = editorContainerRef.current.querySelector('.ProseMirror');
                if (!editorContent) {
                    return;
                }

                // Count block elements (paragraphs, headings, list items)
                const paragraphs = editorContent.querySelectorAll('p');
                const headings = editorContent.querySelectorAll('h1, h2, h3, h4, h5, h6');
                const listItems = editorContent.querySelectorAll('li');
                
                const totalLines = paragraphs.length + headings.length + listItems.length;

                // Calculate pages (55 lines per page)
                const linesPerPage = 55;
                const calculatedPages = Math.max(1, Math.ceil(totalLines / linesPerPage));
                
                setPageCount(calculatedPages);
            } catch (error) {
                console.error('Error calculating page count:', error);
            }
        };

        // Use MutationObserver to watch for content changes
        const observer = new MutationObserver(() => {
            calculatePages();
        });

        if (editorContainerRef.current) {
            const editorContent = editorContainerRef.current.querySelector('.ProseMirror');
            if (editorContent) {
                observer.observe(editorContent, {
                    childList: true,
                    subtree: true,
                    characterData: true,
                });
                
                // Initial calculation
                calculatePages();
            }
        }

        return () => {
            observer.disconnect();
        };
    }, [editor, contentLoaded]);

    return {
        pageCount,
        editorContainerRef,
    };
};
